import { createThreadBehaviour } from './shared-responder-v2-core-thread-behaviour';
import type { ResponderDraftEvent } from './shared-responder-v2-core-thread-behaviour';
import { clampRatingValue } from './shared-responder-v2-core-thread-rating';
import {
  MATRIX_NOT_APPLICABLE,
  THREAD_KINDS,
} from './shared-responder-v2-core-thread.consts';
import type {
  MatrixRowValue,
  QuestionSpec,
  ResponderAnswer,
} from './shared-responder-v2-core-thread.types';

type MatrixDraft = Extract<ResponderAnswer, { kind: 'matrix' }>;

export function emptyMatrixDraft(spec: QuestionSpec): MatrixDraft {
  const rows: Record<string, MatrixRowValue> = {};
  for (const subQuestion of spec.subQuestions) {
    rows[subQuestion.id] = null;
  }
  return { kind: 'matrix', rows };
}

export function reduceMatrixDraft(
  draft: ResponderAnswer | null,
  event: ResponderDraftEvent,
  spec: QuestionSpec
): ResponderAnswer | null {
  if (event.type !== 'matrixRowPicked') {
    return draft;
  }

  if (
    !spec.subQuestions.some(
      (subQuestion) => subQuestion.id === event.subQuestionId
    )
  ) {
    return draft;
  }

  const base = draft?.kind === 'matrix' ? draft : emptyMatrixDraft(spec);

  if (event.value === MATRIX_NOT_APPLICABLE) {
    // Only offered when the question declares an opt-out label; picking it
    // without one would store a value no consumer expects.
    if (spec.notApplicableLabel === null) {
      return draft;
    }

    return {
      kind: 'matrix',
      rows: { ...base.rows, [event.subQuestionId]: MATRIX_NOT_APPLICABLE },
    };
  }

  if (event.value === null || !Number.isFinite(event.value)) {
    return draft;
  }

  return {
    kind: 'matrix',
    rows: {
      ...base.rows,
      [event.subQuestionId]: clampRatingValue(event.value, spec),
    },
  };
}

export function isMatrixDraftComplete(
  draft: ResponderAnswer | null,
  spec: QuestionSpec
): boolean {
  if (spec.subQuestions.length === 0) {
    return true;
  }

  if (draft?.kind !== 'matrix') {
    return false;
  }

  return spec.subQuestions.every(
    (subQuestion) => draft.rows[subQuestion.id] != null
  );
}

export const MATRIX_THREAD_BEHAVIOUR = createThreadBehaviour({
  kind: THREAD_KINDS.Matrix,
  advancesOnSurfacePick: true,
  emptyDraft: emptyMatrixDraft,
  reduceDraft: reduceMatrixDraft,
  isDraftComplete: isMatrixDraftComplete,
});
